import React, { useState, useEffect } from "react";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  PartitionOutlined,
  RightOutlined,
  DownOutlined,
} from "@ant-design/icons";
import MarkdownRender from "@/components/XAdkChatbot/components/MarkdownRender";
import FunctionCallRender from "@/components/XAdkChatbot/components/FunctionCallRender";
import { useStyles } from "./styles";
import {
  XAdkThoughtChainProps,
  ThoughtChainItemType,
  ThoughtChainStatus,
  TimelineItemProps,
  FunctionCall,
  IMessage,
  RenderFunctionCall,
  ToolRenderKind,
} from "@/types";
import { defaultToolKindResolver } from "@/types/FunctionCallRender";
import { cleanProcessText } from "@/utils";

const TimelineItem = ({
  icon,
  title,
  children,
  collapsible = true,
  defaultOpen = true,
  blink,
}: TimelineItemProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const styles = useStyles();

  return (
    <div className={styles.timelineItem}>
      <div className={styles.timelineLeft}>
        {icon}
        <div className={styles.timelineConnector} />
      </div>
      <div className={styles.timelineRight}>
        <div
          className={styles.collapsibleHeader}
          onClick={() => (collapsible ? setIsOpen(!isOpen) : undefined)}
        >
          <div className={blink ? styles.blink : undefined}>{title}</div>
          {collapsible && (
            <span className={styles.arrowIcon}>
              {isOpen ? <DownOutlined /> : <RightOutlined />}
            </span>
          )}
        </div>

        {isOpen && <div className={styles.stepContent}>{children}</div>}
      </div>
    </div>
  );
};

const TimelineTextItem: React.FC<{
  item: ThoughtChainItemType;
  blink?: boolean;
}> = ({ item, blink }) => {
  return (
    <TimelineItem
      icon={
        <span className={useStyles().timelineIcon}>
          <PartitionOutlined />
        </span>
      }
      title={<span className={useStyles().titleText}>{item.title}</span>}
      blink={blink}
    >
      <MarkdownRender text={cleanProcessText(item.content)} />
    </TimelineItem>
  );
};

const TimelineToolItem: React.FC<{
  msg: IMessage;
  collapsible?: boolean;
  showFnCallDetail: boolean;
  onConfirm?: (fnCall: FunctionCall, confirmed: boolean) => void;
  blink?: boolean;
  renderFunctionCall?: RenderFunctionCall;
  toolKindResolver: (name: string | undefined) => ToolRenderKind;
  strategies?: XAdkThoughtChainProps["strategies"];
}> = ({
  msg,
  collapsible,
  onConfirm,
  showFnCallDetail,
  blink,
  renderFunctionCall,
  toolKindResolver,
  strategies,
}) => {
  const styles = useStyles();
  const fnName = msg.functionCall?.name;
  const kind = toolKindResolver(fnName);
  const needsConfirm = msg.functionCall && !msg.functionResponse;
  const isDone = !!msg.functionResponse;

  const toolName =
    msg.functionCall?.args?.componentName ||
    msg.functionCall?.name ||
    "组件执行";

  const statusInfo =
    msg.functionResponse?.response?.processData ||
    msg.functionResponse?.response?.output;

  const titleNode = (
    <div className={styles.toolTitleGroup}>
      <span className={styles.toolName}>{toolName}</span>
      {isDone && <span className={styles.toolStatusTag}>执行成功</span>}
    </div>
  );

  return (
    <TimelineItem
      icon={
        <span className={styles.timelineIconTool}>
          <ThunderboltOutlined />
        </span>
      }
      title={titleNode}
      collapsible={collapsible}
      defaultOpen={needsConfirm}
      blink={blink}
    >
      {statusInfo && typeof statusInfo === "string" && (
        <div className={styles.statusInfo}>{statusInfo}</div>
      )}
      {renderFunctionCall?.(msg) ?? (
        <FunctionCallRender
          msg={msg}
          showDetail={showFnCallDetail}
          onConfirm={onConfirm}
          kind={kind}
          renderApproval={strategies?.renderApproval}
          renderHandoff={strategies?.renderHandoff}
        />
      )}
    </TimelineItem>
  );
};

const StatusIcon: React.FC<{ status: ThoughtChainStatus }> = ({ status }) => {
  switch (status) {
    case "loading":
      return <SyncOutlined spin style={{ color: "#1677ff" }} />;
    case "success":
      return <CheckCircleFilled style={{ color: "#52c41a" }} />;
    case "error":
      return <CloseCircleFilled style={{ color: "#ff4d4f" }} />;
    case "pending":
      return <ClockCircleOutlined style={{ color: "#8c8c8c" }} />;
  }
};

const XAdkThoughtChain: React.FC<XAdkThoughtChainProps> = ({
  collapsible: showCollapsibleHeader = false,
  status,
  className,
  style,
  defaultOpen = false,
  showFnCallDetail = false,
  title = "思维链已完成",
  items = [],
  loading,
  blink = false,
  onConfirm,
  renderFunctionCall,
  toolKindResolver = defaultToolKindResolver,
  strategies,
}) => {
  const styles = useStyles();

  // status 优先，loading 向后兼容
  const effectiveStatus: ThoughtChainStatus =
    status ?? (loading ? "loading" : "success");
  const isLoading = effectiveStatus === "loading";

  const [isOpen, setIsOpen] = useState(defaultOpen);
  useEffect(() => {
    if (isLoading) Promise.resolve().then(() => setIsOpen(true));
  }, [isLoading]);

  const shouldBlink = blink && isLoading;

  const renderTimeline = () => (
    <div className={styles.timeline}>
      {items.map((item, i) =>
        item.type === "text" ? (
          <TimelineTextItem
            key={item.key || i}
            item={item}
            blink={shouldBlink}
          />
        ) : (
          <TimelineToolItem
            key={`tool-${item.key || i}`}
            msg={item.msg!}
            collapsible={item.collapsible}
            showFnCallDetail={showFnCallDetail}
            onConfirm={onConfirm}
            blink={shouldBlink}
            renderFunctionCall={renderFunctionCall}
            toolKindResolver={toolKindResolver}
            strategies={strategies}
          />
        ),
      )}
    </div>
  );

  // 不显示外层 header，直接渲染 timeline
  if (!showCollapsibleHeader) {
    return (
      <div
        className={`${styles.thoughtChainBlock}${className ? ` ${className}` : ""}`}
        style={style}
      >
        <div className={styles.thinkingBody}>{renderTimeline()}</div>
      </div>
    );
  }

  // 显示可折叠 header
  return (
    <div
      className={`${styles.thoughtChainBlock}${className ? ` ${className}` : ""}`}
      style={style}
    >
      <div className={styles.thoughtHeader} onClick={() => setIsOpen(!isOpen)}>
        <div className={styles.headerLeft}>
          <div className={styles.statusIcon}>
            <StatusIcon status={effectiveStatus} />
          </div>
          <span
            className={`${styles.headerTitle}${shouldBlink ? ` ${styles.blink}` : ""}`}
          >
            {isLoading ? "深度思考与执行中..." : title}
          </span>
          <div className={`${styles.caret} ${isOpen ? "open" : ""}`}>
            <RightOutlined />
          </div>
        </div>
      </div>
      {isOpen && (
        <div className={styles.thinkingBody}>{renderTimeline()}</div>
      )}
    </div>
  );
};

export default XAdkThoughtChain;
